import React, { HTMLAttributes } from 'react';
import { clsx } from 'clsx';

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'outlined' | 'elevated';
  padding?: 'none' | 'sm' | 'md' | 'lg';
}

export function Card({ 
  className, 
  variant = 'default', 
  padding = 'md',
  children, 
  ...props 
}: CardProps) {
  const baseClasses = 'bg-white rounded-lg';
  
  const variants = {
    default: 'shadow',
    outlined: 'border border-gray-200',
    elevated: 'shadow-lg',
  };
  
  const paddings = {
    none: '',
    sm: 'p-3 sm:p-3',
    md: 'p-4 sm:p-4',
    lg: 'p-4 sm:p-6', // More mobile-friendly padding
  };
  
  const classes = clsx(
    baseClasses,
    variants[variant],
    paddings[padding],
    className
  );
  
  return (
    <div className={classes} {...props}>
      {children}
    </div>
  );
}

interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  title?: string; // Used for card title display
  subtitle?: string; // Used for card subtitle display
  action?: React.ReactNode; // Used for action buttons or elements
}

export function CardHeader({
  className,
  title,
  subtitle,
  action,
  children,
  ...props
}: CardHeaderProps) {
  const classes = clsx('border-b border-gray-200 pb-3 mb-4', className);

  return (
    <div className={classes} {...props}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          {title && (
            <h3 className="text-lg font-semibold text-gray-900">
              {title}
            </h3>
          )}
          {subtitle && (
            <p className="text-sm text-gray-600 mt-1">
              {subtitle}
            </p>
          )}
        </div>
        {action && (
          <div className="ml-4 flex-shrink-0">
            {action}
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

interface CardContentProps extends HTMLAttributes<HTMLDivElement> {}

export function CardContent({ className, children, ...props }: CardContentProps) {
  return (
    <div className={className} {...props}>
      {children}
    </div>
  );
}

interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {}

export function CardFooter({ className, children, ...props }: CardFooterProps) {
  const classes = clsx('border-t border-gray-200 pt-3 mt-4', className);
  
  return (
    <div className={classes} {...props}>
      {children}
    </div>
  );
}
